import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/errors";

export default function PlatformTenantDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/platform/tenants/${slug}`);
    setData(data);
    setDraft({
      name: data.name,
      primary_color: data.branding.primary_color,
      accent_color: data.branding.accent_color,
      logo_url: data.branding.logo_url || "",
      logo_file: null,
    });
  };
  useEffect(() => { load(); }, [slug]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      if (draft.name !== data.name) fd.append("name", draft.name);
      if (draft.primary_color !== data.branding.primary_color) fd.append("primary_color", draft.primary_color);
      if (draft.accent_color !== data.branding.accent_color) fd.append("accent_color", draft.accent_color);
      if (draft.logo_file) fd.append("logo_file", draft.logo_file);
      else if (draft.logo_url !== (data.branding.logo_url || "")) fd.append("logo_url", draft.logo_url);
      await api.patch(`/platform/tenants/${slug}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Tenant updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Update failed");
    } finally { setSaving(false); }
  };

  const toggleStatus = async () => {
    const next = data.status === "disabled" ? "active" : "disabled";
    const label = next === "disabled" ? "Disable" : "Re-enable";
    if (!confirm(`${label} tenant "${data.name}"?${next === "disabled" ? " Members will be signed out and unable to switch back in until re-enabled." : ""}`)) return;
    try {
      await api.patch(`/platform/tenants/${slug}`, { status: next }, { headers: { "Content-Type": "application/json" } });
      toast.success(`Tenant ${next === "disabled" ? "disabled" : "re-enabled"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Status update failed");
    }
  };

  if (!data || !draft) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div data-testid="platform-tenant-detail" className="mx-auto w-full max-w-5xl space-y-6">
      <Breadcrumbs />
      <div className="flex items-center gap-4">
        {data.branding.logo_url ? (
          <img src={data.branding.logo_url} className="h-10 w-auto max-w-[14rem] object-contain" alt="" />
        ) : (
          <span className="inline-block h-8 w-8 rounded" style={{ background: data.branding.primary_color }} />
        )}
        <div>
          <h2 className="font-display text-2xl font-semibold text-slate-900">{data.name}</h2>
          <div className="text-xs font-mono text-slate-500">/{data.slug}</div>
        </div>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-600" data-testid="detail-status-pill">{data.status}</span>
        {data.is_platform && <span className="rounded bg-slate-900 px-1 py-0.5 text-[9px] text-amber-300">PLATFORM</span>}
        {!data.is_platform && (
          <Button
            type="button"
            variant={data.status === "disabled" ? "default" : "outline"}
            size="sm"
            onClick={toggleStatus}
            data-testid="detail-toggle-status"
            className={data.status === "disabled" ? "ml-auto bg-emerald-600 hover:bg-emerald-700" : "ml-auto border-amber-500 text-amber-700 hover:bg-amber-50"}
          >
            {data.status === "disabled" ? "Re-enable tenant" : "Disable tenant"}
          </Button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={save} className="space-y-3 rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-sm font-semibold text-slate-900">Branding</h3>
          <div><Label>Name</Label>
            <Input value={draft.name} data-testid="detail-name" onChange={e => setDraft({...draft, name:e.target.value})}/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Primary</Label>
              <Input type="color" value={draft.primary_color} data-testid="detail-primary" onChange={e => setDraft({...draft, primary_color:e.target.value})}/>
            </div>
            <div><Label>Accent</Label>
              <Input type="color" value={draft.accent_color} data-testid="detail-accent" onChange={e => setDraft({...draft, accent_color:e.target.value})}/>
            </div>
          </div>
          <div><Label>Logo</Label>
            <div className="flex gap-2">
              <Input type="file" accept=".svg,.png,.jpg,.jpeg,.webp" data-testid="detail-logo-file"
                onChange={e => setDraft({...draft, logo_file:e.target.files[0]||null, logo_url:""})}/>
              <Input placeholder="…or URL" value={draft.logo_url} data-testid="detail-logo-url"
                onChange={e => setDraft({...draft, logo_url:e.target.value, logo_file:null})}/>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-400">Preview</div>
            <div className="flex items-center gap-2">
              <span className="inline-block h-5 w-5 rounded" style={{background: draft.primary_color}}/>
              <span className="text-sm font-semibold" style={{color: draft.primary_color}}>{draft.name}</span>
              <span className="ml-auto h-4 w-4 rounded-full" style={{background: draft.accent_color}}/>
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="detail-save" className="bg-slate-900 hover:bg-slate-800">{saving?"Saving…":"Save branding"}</Button>
        </form>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="mb-3 font-display text-sm font-semibold text-slate-900">Stats</h3>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="text-2xl font-semibold">{data.stats.vehicles}</div><div className="text-[10px] uppercase text-slate-500">Vehicles</div></div>
            <div><div className="text-2xl font-semibold">{data.stats.entries_last_30d}</div><div className="text-[10px] uppercase text-slate-500">Entries 30d</div></div>
            <div><div className="text-2xl font-semibold">{data.stats.audit_last_7d}</div><div className="text-[10px] uppercase text-slate-500">Audit 7d</div></div>
          </div>
          <h3 className="mt-5 mb-2 font-display text-sm font-semibold text-slate-900">Apps granted</h3>
          <div className="flex flex-wrap gap-2">
            {data.apps.length === 0 ? <span className="text-xs text-slate-400">No grants. <Link to="/platform/apps" className="underline">Manage →</Link></span>
              : data.apps.map(a => <span key={a.id} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs">{a.name}</span>)}
          </div>
        </div>
      </div>

      <section>
        <h3 className="mb-3 font-display text-sm font-semibold text-slate-900">Members ({data.members.length})</h3>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-2 text-left">Email</th><th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-left">Role</th><th className="px-4 py-2 text-left">Status</th></tr>
            </thead>
            <tbody>
              {data.members.map(m => (
                <tr key={m.user_id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs">{m.email}</td>
                  <td className="px-4 py-2">{m.full_name}</td>
                  <td className="px-4 py-2 text-xs">{m.role}</td>
                  <td className="px-4 py-2 text-xs">{m.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
